import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Package, ChevronRight, Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import { ProgramsListProps } from './types';
import { getColorForIndex, getInitials } from './constants';

const ProgramsList: React.FC<ProgramsListProps> = ({
  programs,
  selectedMedications,
  searchTerm,
  loading,
  priorityLevel,
  onSearchChange,
  onProgramSelect,
  onClearSelections,
  onRemoveMedication,
  onPriorityChange,
}) => {
  const selectedCount = Object.keys(selectedMedications).length;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredPrograms = programs.filter(program => {
    if (!normalizedSearch) return true;
    const searchableText = `${program.title || ''} ${program.subtitle || ''}`.toLowerCase();
    return searchableText.includes(normalizedSearch);
  });

  const getSelectedCountByProgram = (programId: string) => {
    return Object.values(selectedMedications).filter(sel => sel.program_id === programId).length;
  };

  return (
    <div className='space-y-4'>
      {/* Selected Medications Summary */}
      {selectedCount > 0 && (
        <div className='rounded-lg border border-brand-teal/20 bg-gradient-to-r from-brand-teal/5 to-brand-teal/10 p-4'>
          <div className='mb-3 flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-brand-teal/20'>
                <Check className='h-4 w-4 text-brand-teal' />
              </div>
              <div>
                <h4 className='text-sm font-semibold text-slate-900'>
                  {selectedCount} Medication{selectedCount !== 1 ? 's' : ''} Selected
                </h4>
                <p className='text-xs text-slate-500'>From multiple programs</p>
              </div>
            </div>
            <Button
              variant='ghost'
              size='sm'
              className='h-7 text-xs text-red-500 hover:bg-red-50 hover:text-red-600'
              onClick={onClearSelections}
            >
              Clear All
            </Button>
          </div>
          <div className='flex flex-wrap gap-2'>
            {Object.entries(selectedMedications).map(([medId, selection]) => (
              <Badge
                key={medId}
                variant='secondary'
                className='flex items-center gap-1.5 border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50'
              >
                <span className='max-w-[120px] truncate'>{selection.medication_name}</span>
                <span className='text-slate-400'>•</span>
                <span className='text-slate-500'>{selection.dose}</span>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onRemoveMedication(medId);
                  }}
                  className='ml-1 text-slate-400 transition-colors hover:text-red-500'
                >
                  <X className='h-3 w-3' />
                </button>
              </Badge>
            ))}
          </div>

          {/* Priority Level Selector */}
          <div className='mt-3 border-t border-brand-teal/20 pt-3'>
            <div className='flex items-center justify-between'>
              <Label className='text-xs font-medium text-slate-600'>Priority Level</Label>
              <Select value={String(priorityLevel)} onValueChange={value => onPriorityChange(Number(value))}>
                <SelectTrigger className='h-8 w-[160px] border-slate-200 bg-white text-xs'>
                  <SelectValue placeholder='Select priority' />
                </SelectTrigger>
                <SelectContent className='min-w-[160px]'>
                  <SelectItem value='-1'>
                    <span className='flex items-center gap-2 whitespace-nowrap'>
                      <span className='h-2 w-2 flex-shrink-0 rounded-full bg-slate-400'></span>
                      Low Precedence
                    </span>
                  </SelectItem>
                  <SelectItem value='1'>
                    <span className='flex items-center gap-2 whitespace-nowrap'>
                      <span className='h-2 w-2 flex-shrink-0 rounded-full bg-green-500'></span>
                      Normal
                    </span>
                  </SelectItem>
                  <SelectItem value='2'>
                    <span className='flex items-center gap-2 whitespace-nowrap'>
                      <span className='h-2 w-2 flex-shrink-0 rounded-full bg-amber-500'></span>
                      Elevated
                    </span>
                  </SelectItem>
                  <SelectItem value='3'>
                    <span className='flex items-center gap-2 whitespace-nowrap'>
                      <span className='h-2 w-2 flex-shrink-0 rounded-full bg-red-500'></span>
                      Urgent
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
        <Input
          type='text'
          placeholder='Search programs...'
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value.trimStart())}
          className='rounded-lg border-slate-200 bg-white pl-10'
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className='grid grid-cols-2 gap-3'>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className='space-y-3 rounded-lg border border-slate-200 bg-white p-4'>
              <div className='flex items-start gap-3'>
                <Skeleton className='h-10 w-10 rounded-lg' />
                <div className='flex-1 space-y-2'>
                  <Skeleton className='h-4 w-24' />
                  <Skeleton className='h-3 w-16' />
                </div>
              </div>
              <Skeleton className='h-3 w-full' />
              <Skeleton className='h-3 w-3/4' />
            </div>
          ))}
        </div>
      )}

      {/* Programs Grid */}
      {!loading && filteredPrograms.length > 0 && (
        <div className='grid grid-cols-2 gap-3'>
          {filteredPrograms.map((program, index) => {
            const colors = getColorForIndex(index);
            const programSelectionCount = getSelectedCountByProgram(program.id);

            return (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
              >
                <Card
                  className={`group relative h-full cursor-pointer overflow-hidden border-slate-200 bg-white transition-all duration-300 hover:border-slate-300 hover:shadow-md ${programSelectionCount > 0 ? 'border-brand-teal/30 ring-2 ring-brand-teal/30' : ''}`}
                  onClick={() => onProgramSelect(program.id)}
                >
                  {/* Top accent bar */}
                  <div
                    className={`absolute left-0 right-0 top-0 h-1 ${programSelectionCount > 0 ? 'bg-brand-teal' : colors.bg}`}
                  />

                  {/* Selection badge */}
                  {programSelectionCount > 0 && (
                    <div className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-teal text-xs font-semibold text-white'>
                      {programSelectionCount}
                    </div>
                  )}

                  <CardContent className='p-4 pt-5'>
                    <div className='mb-3 flex items-start gap-3'>
                      {/* Program Image or Letter Icon */}
                      {program.image_url ? (
                        <div className='h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg border border-slate-100 transition-transform duration-300 group-hover:scale-105'>
                          <img
                            src={program.image_url}
                            alt={program.title}
                            className='h-full w-full object-cover'
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.classList.add(colors.bg);
                              e.currentTarget.parentElement!.innerHTML = `<span class="${colors.text} font-montserrat text-base font-semibold flex items-center justify-center h-full w-full">${getInitials(program.title)}</span>`;
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className={`h-10 w-10 ${colors.bg} flex items-center justify-center rounded-lg transition-transform duration-300 group-hover:scale-105`}
                        >
                          <span className={`${colors.text} font-montserrat text-base font-semibold`}>
                            {getInitials(program.title)}
                          </span>
                        </div>
                      )}

                      <div className='min-w-0 flex-1'>
                        <div className='flex items-center gap-1.5'>
                          <h4 className='line-clamp-1 text-sm font-semibold text-slate-900'>
                            {program.title}
                          </h4>
                          {program.display_mode === 'protocol_showcase' && (
                            <span className='inline-flex flex-shrink-0 items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20'>
                              Protocol
                            </span>
                          )}
                        </div>
                        <p className='mt-0.5 line-clamp-1 text-xs text-slate-500'>
                          {program.subtitle}
                        </p>
                      </div>
                    </div>

                    <div className='flex items-center justify-between border-t border-slate-100 pt-3'>
                      <span
                        className={`flex items-center gap-0.5 text-xs font-medium ${programSelectionCount > 0 ? 'text-brand-teal' : colors.text} transition-all group-hover:gap-1`}
                      >
                        {programSelectionCount > 0 ? 'Edit' : 'View'}
                        <ChevronRight className='h-3 w-3' />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredPrograms.length === 0 && (
        <div className='py-12 text-center'>
          <Package className='mx-auto mb-3 h-10 w-10 text-slate-300' />
          <p className='text-sm text-slate-500'>
            {searchTerm ? 'No programs found' : 'No programs available'}
          </p>
          {searchTerm && (
            <Button
              variant='ghost'
              size='sm'
              className='mt-3 text-primary'
              onClick={() => onSearchChange('')}
            >
              Clear search
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgramsList;

